// src/components/vendor/ProductForm.jsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  Stack,
  Typography,
  FormControlLabel,
  Switch,
  Chip,
  Autocomplete,
  Grid,
  Paper,
  IconButton,
  Avatar,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Add,
  Close,
  CloudUpload,
  Delete,
} from '@mui/icons-material';
import { useOptions } from '../../context/OptionsContext';

const ProductForm = ({ product, onSave, onCancel }) => {
  const { 
    categories, 
    sizes, 
    colors, 
    badges, 
    loading: optionsLoading,
    fetchAllOptions 
  } = useOptions();

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    originalPrice: '',
    stock: '',
    category: '',
    subcategory: '',
    sizes: [],
    colors: [],
    badge: '',
    isFeatured: false,
    images: [],
    thumbnail: '',
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  // Fetch options if not loaded
  useEffect(() => {
    if (categories.length === 0 && sizes.length === 0 && colors.length === 0) {
      fetchAllOptions();
    }
  }, []);

  // Populate form if editing
  useEffect(() => {
    if (product) {
      setFormData({
        name: product.name || '',
        description: product.description || '',
        price: product.price?.toString() || '',
        originalPrice: product.originalPrice?.toString() || '',
        stock: product.stock?.toString() || '',
        category: product.category || '',
        subcategory: product.subcategory || '',
        sizes: product.sizes || [],
        colors: product.colors || [],
        badge: product.badge || '',
        isFeatured: product.isFeatured || false,
        images: product.images || [],
        thumbnail: product.thumbnail || '',
      });
    }
  }, [product]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleSizeToggle = (size) => {
    setFormData((prev) => ({
      ...prev,
      sizes: prev.sizes.includes(size)
        ? prev.sizes.filter((s) => s !== size)
        : [...prev.sizes, size],
    }));
  };

  const handleColorToggle = (color) => {
    setFormData((prev) => ({
      ...prev,
      colors: prev.colors.includes(color)
        ? prev.colors.filter((c) => c !== color)
        : [...prev.colors, color],
    }));
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Product name is required';
    if (!formData.price || parseFloat(formData.price) <= 0) {
      newErrors.price = 'Valid price is required';
    }
    if (!formData.stock || parseInt(formData.stock) < 0) {
      newErrors.stock = 'Valid stock quantity is required';
    }
    if (!formData.category) newErrors.category = 'Category is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    setLoading(true);
    try {
      await onSave({
        ...formData,
        price: parseFloat(formData.price),
        originalPrice: formData.originalPrice ? parseFloat(formData.originalPrice) : null,
        stock: parseInt(formData.stock),
      });
    } catch (error) {
      console.error('Error saving product:', error);
    } finally {
      setLoading(false);
    }
  };

  // Color mapping for display
  const getColorCode = (colorName) => {
    const colorMap = {
      'Red': '#f44336',
      'Blue': '#2196f3',
      'Green': '#4caf50',
      'Yellow': '#ffeb3b',
      'Black': '#000000',
      'White': '#ffffff',
      'Gray': '#9e9e9e',
      'Pink': '#e91e63',
      'Purple': '#9c27b0',
      'Orange': '#ff9800',
      'Brown': '#795548',
      'Navy': '#1a237e',
      'Emerald': '#2e7d32',
      'Burgundy': '#880e4f',
    };
    return colorMap[colorName] || '#9e9e9e';
  };

  if (optionsLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxHeight: '70vh', overflow: 'auto' }}>
      <Stack spacing={3} sx={{ p: 1 }}>
        {/* Product Name */}
        <TextField
          fullWidth
          label="Product Name"
          name="name"
          value={formData.name}
          onChange={handleChange}
          error={!!errors.name}
          helperText={errors.name}
          required
        />

        {/* Description */}
        <TextField
          fullWidth
          label="Description"
          name="description"
          value={formData.description}
          onChange={handleChange}
          multiline
          rows={4}
        />

        {/* Pricing */}
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <TextField
              fullWidth
              label="Price"
              name="price"
              type="number"
              value={formData.price}
              onChange={handleChange}
              error={!!errors.price}
              helperText={errors.price}
              required
              InputProps={{ startAdornment: <Typography sx={{ mr: 1 }}>$</Typography> }}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth
              label="Original Price (Optional)"
              name="originalPrice"
              type="number"
              value={formData.originalPrice}
              onChange={handleChange}
              InputProps={{ startAdornment: <Typography sx={{ mr: 1 }}>$</Typography> }}
            />
          </Grid>
        </Grid>

        {/* Stock */}
        <TextField
          fullWidth
          label="Stock Quantity"
          name="stock"
          type="number"
          value={formData.stock}
          onChange={handleChange}
          error={!!errors.stock}
          helperText={errors.stock}
          required
        />

        {/* Dynamic Category */}
        <Autocomplete
          options={categories}
          getOptionLabel={(option) => option.name || option}
          value={categories.find(c => c.id === formData.category || c.name === formData.category) || null}
          onChange={(_, newValue) => {
            setFormData((prev) => ({
              ...prev,
              category: newValue ? (newValue.id || newValue.name || newValue) : '',
            }));
            if (errors.category) setErrors((prev) => ({ ...prev, category: '' }));
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Category"
              error={!!errors.category}
              helperText={errors.category}
              required
            />
          )}
        />

        {/* Subcategory */}
        <TextField
          fullWidth
          label="Subcategory (Optional)"
          name="subcategory"
          value={formData.subcategory}
          onChange={handleChange}
        />

        {/* Dynamic Sizes */}
        <Box>
          <Typography variant="subtitle2" gutterBottom>
            Sizes
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap">
            {sizes.map((size) => (
              <Chip
                key={size.id || size}
                label={size.name || size}
                onClick={() => handleSizeToggle(size.name || size)}
                color={formData.sizes.includes(size.name || size) ? 'primary' : 'default'}
                clickable
                sx={{ mb: 1 }}
              />
            ))}
          </Stack>
        </Box>

        {/* Dynamic Colors */}
        <Box>
          <Typography variant="subtitle2" gutterBottom>
            Colors
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap">
            {colors.map((color) => (
              <Chip
                key={color.id || color}
                label={
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Box
                      sx={{
                        width: 16,
                        height: 16,
                        borderRadius: '50%',
                        bgcolor: getColorCode(color.name || color),
                        border: '1px solid',
                        borderColor: 'grey.300',
                      }}
                    />
                    <span>{color.name || color}</span>
                  </Stack>
                }
                onClick={() => handleColorToggle(color.name || color)}
                color={formData.colors.includes(color.name || color) ? 'primary' : 'default'}
                clickable
                sx={{ mb: 1 }}
              />
            ))}
          </Stack>
        </Box>

        {/* Dynamic Badge */}
        <Autocomplete
          options={badges}
          getOptionLabel={(option) => option.name || option}
          value={badges.find(b => b.name === formData.badge) || formData.badge || null}
          onChange={(_, newValue) => {
            setFormData((prev) => ({
              ...prev,
              badge: newValue ? (newValue.name || newValue) : '',
            }));
          }}
          renderInput={(params) => <TextField {...params} label="Badge (Optional)" />}
        />

        {/* Featured Switch */}
        <FormControlLabel
          control={
            <Switch
              checked={formData.isFeatured}
              onChange={handleChange}
              name="isFeatured"
              color="primary"
            />
          }
          label="Featured Product"
        />

        {/* Image Upload */}
        <Box>
          <Typography variant="subtitle2" gutterBottom>
            Product Images
          </Typography>
          <Paper
            sx={{
              p: 3,
              border: '2px dashed',
              borderColor: 'grey.300',
              bgcolor: 'grey.50',
              textAlign: 'center',
            }}
          >
            <CloudUpload sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
            <Typography variant="body2" color="text.secondary">
              Click to upload or drag and drop images here
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Supported formats: JPG, PNG, WEBP (Max 5MB)
            </Typography>
            <Button variant="outlined" sx={{ mt: 2 }}>
              Choose Files
            </Button>
          </Paper>
          <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap' }}>
            {formData.images?.map((image, index) => (
              <Box key={index} sx={{ position: 'relative' }}>
                <Avatar variant="rounded" src={image} sx={{ width: 64, height: 64 }} />
                <IconButton
                  size="small"
                  sx={{
                    position: 'absolute',
                    top: -8,
                    right: -8,
                    bgcolor: 'error.main',
                    color: 'white',
                    '&:hover': { bgcolor: 'error.dark' },
                  }}
                  onClick={() => {
                    setFormData((prev) => ({
                      ...prev,
                      images: prev.images.filter((_, i) => i !== index),
                    }));
                  }}
                >
                  <Close fontSize="small" />
                </IconButton>
              </Box>
            ))}
          </Stack>
        </Box>

        {/* Actions */}
        <Stack direction="row" spacing={2} justifyContent="flex-end">
          <Button onClick={onCancel} disabled={loading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading ? 'Saving...' : product ? 'Update Product' : 'Add Product'}
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
};

export default ProductForm;